<template>
  <header
    class="fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out"
    :class="navbarClass"
  >
    <nav class="relative">
      <!-- Animated gradient background for scrolled state (already light) -->
      <div
        v-if="isScrolled"
        class="absolute inset-0 bg-gradient-to-r from-[#f4f4f4]/95 via-[#f8f9fa]/95 to-[#e9ecef]/95 animate-gradient-x"
      ></div>

      <!-- Glass morphism background -->
      <div
        class="absolute inset-0 backdrop-blur-md transition-all duration-500"
        :class="isScrolled ? 'bg-white/80' : 'bg-transparent'"
      ></div>

      <div class="relative container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
        <div
          class="flex items-center justify-between transition-all duration-500"
          :class="isScrolled ? 'h-16 sm:h-18' : 'h-20 sm:h-24'"
        >
          <!-- Logo (already has good contrast) -->
          <div class="flex-shrink-0 group">
            <router-link to="/" class="flex items-center space-x-2">
              <div
                class="rounded-lg sm:rounded-xl bg-gradient-to-br from-[#ff66004d] to-[#ffb47c] flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105"
                :class="isScrolled ? 'w-8 h-8 sm:w-9 sm:h-9' : 'w-10 h-10 sm:w-11 sm:h-11'"
              >
                <TorukLogo
                  class="text-white transition-all duration-300"
                  :class="isScrolled ? 'w-4 h-4 sm:w-5 sm:h-5' : 'w-5 h-5 sm:w-6 sm:h-6'"
                />
              </div>
              <div class="flex items-baseline">
                <span
                  class="font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#ff6600] to-[#fb9241] transition-all duration-300"
                  :class="isScrolled ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl'"
                >
                  toruk
                </span>
                <span
                  class="font-semibold text-[#ff6f00] ml-1 transition-all duration-300"
                  :class="isScrolled ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'"
                >
                  MAGIC&trade;
                </span>
              </div>
            </router-link>
          </div>

          <!-- Desktop Navigation -->
          <div class="hidden md:flex items-center">
            <div
              class="flex items-center space-x-1 rounded-2xl p-1 backdrop-blur-sm border transition-all duration-300"
              :class="
                isScrolled ? 'bg-white/40 border-gray-200/50' : 'bg-gray-600/5 border-gray-500/20' // CHANGED for better visibility on light bg
              "
            >
              <router-link
                v-for="item in navItems"
                :key="item.name"
                :to="item.path"
                class="relative px-4 lg:px-5 py-2 sm:py-2.5 rounded-[10px] text-sm font-medium transition-all duration-300 group"
                :class="
                  isScrolled
                    ? 'text-[#0b105d]/80 hover:text-[#0b105d]'
                    : 'text-[#0b105d]/70 hover:text-[#fe6d0d]' // CHANGED to dark text
                "
                :active-class="'router-link-active'"
              >
                <div
                  class="absolute inset-0 rounded-[10px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out"
                  :class="[
                    { '!opacity-0': isActive(item.path) },
                    isScrolled
                      ? 'bg-gradient-to-r from-gray-100 to-gray-50'
                      : 'bg-gradient-to-r from-[#0b105d]/5 to-[#0b105d]/10', // CHANGED for subtle dark hover bg
                  ]"
                ></div>
                <!-- Active state remains good as it uses brand colors -->
                <div
                  v-if="isActive(item.path)"
                  class="absolute inset-0 bg-gradient-to-r from-[#fe6d0d]/20 to-[#ffb47c]/20 rounded-[10px] shadow-md"
                ></div>
                <div
                  v-if="isActive(item.path)"
                  class="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-3/5 h-0.5 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c] rounded-full"
                ></div>
                <span class="relative z-10">{{ $t(item.name) }}</span>
              </router-link>
            </div>
          </div>

          <!-- Desktop Action Buttons -->
          <div class="hidden md:flex items-center space-x-3 lg:space-x-4">
            <LanguageSwitcher
              class="backdrop-blur-sm rounded-lg xl:rounded-xl hover:scale-105 transition-all duration-300"
              :class="
                isScrolled
                  ? 'bg-white/60 border border-gray-200/50 hover:bg-white/80'
                  : 'bg-gray-600/5 border-gray-500/20 hover:bg-gray-600/10 text-[#0b105d]' // CHANGED for contrast
              "
            />
            <router-link
              to="/contact"
              class="cta-button relative group px-4 lg:px-6 py-2.5 lg:py-3 rounded-lg xl:rounded-xl text-xs sm:text-sm font-semibold text-white overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              <div
                class="absolute inset-0 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c] transition-all duration-300"
              ></div>
              <div
                class="absolute inset-0 bg-gradient-to-r from-[#ffb47c] to-[#fe6d0d] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              ></div>
              <div class="rotating-glow"></div>
              <span class="relative z-10 flex items-center space-x-1.5 sm:space-x-2">
                <span>{{ $t('nav.getStarted') }}</span>
                <ArrowRightIcon
                  class="w-3 h-3 sm:w-4 sm:h-4 transform group-hover:translate-x-0.5 transition-transform duration-300"
                />
              </span>
            </router-link>
          </div>

          <!-- Mobile Controls -->
          <div class="md:hidden flex items-center space-x-2 sm:space-x-3">
            <LanguageSwitcher
              class="backdrop-blur-sm rounded-lg transition-all duration-300"
              :class="
                isScrolled
                  ? 'bg-white/60 border border-gray-200/50'
                  : 'bg-gray-600/5 border-gray-500/20 hover:bg-gray-600/10 text-[#0b105d]' // CHANGED for contrast
              "
            />
            <button
              @click="mobileMenuOpen = !mobileMenuOpen"
              class="relative group p-2.5 sm:p-3 rounded-lg backdrop-blur-sm transition-all duration-300"
              :class="
                isScrolled
                  ? 'bg-white/60 border border-gray-200/50 hover:bg-white/80'
                  : 'bg-gray-600/5 border-gray-500/20 hover:bg-gray-600/10' // CHANGED for contrast
              "
              aria-label="Toggle main menu"
            >
              <span class="sr-only">Open main menu</span>
              <MenuIcon
                v-if="!mobileMenuOpen"
                class="h-5 w-5 sm:h-6 sm:w-6 transition-colors duration-300 text-[#0b105d]"
              />
              <XIcon
                v-else
                class="h-5 w-5 sm:h-6 sm:w-6 transition-colors duration-300 text-[#0b105d]"
              />
            </button>
          </div>
        </div>
      </div>
    </nav>

    <!-- Mobile Menu -->
    <transition
      enter-active-class="transition ease-out duration-300 transform"
      enter-from-class="opacity-0 -translate-y-8 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition ease-in duration-200 transform"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 -translate-y-8 scale-95"
    >
      <div
        v-if="mobileMenuOpen"
        class="md:hidden absolute w-full backdrop-blur-xl border-b shadow-2xl mobile-menu-container"
        :class="
          isScrolled ? 'bg-white/95 border-gray-200/50' : 'bg-[#f8f9fa]/95 border-gray-200/30' // CHANGED for light theme when not scrolled
        "
        @click.self="mobileMenuOpen = false"
      >
        <div
          class="relative mx-3 sm:mx-4 mt-3 sm:mt-4 mb-4 sm:mb-6 rounded-xl sm:rounded-2xl backdrop-blur-md shadow-2xl overflow-hidden border"
          :class="
            isScrolled ? 'bg-white/90 border-gray-200/50' : 'bg-white/80 border-gray-200/40' // CHANGED for light theme when not scrolled
          "
        >
          <!-- Subtle background pattern for mobile menu -->
          <div
            class="absolute inset-0 bg-gradient-to-br from-[#fe6d0d]/5 via-[#ffb47c]/5 to-[#0b105d]/5 opacity-50"
            :class="{ 'opacity-30': !isScrolled }"
          ></div>
          <div class="relative p-3 sm:p-4 md:p-6 space-y-2">
            <router-link
              v-for="(item, index) in navItems"
              :key="item.name"
              :to="item.path"
              @click="toggleMobileMenu"
              class="group mobile-menu-item block relative px-3 sm:px-4 py-3 sm:py-3.5 rounded-lg sm:rounded-xl text-sm sm:text-base font-medium transition-all duration-300 transform hover:scale-[1.01]"
              :class="[
                isScrolled
                  ? 'text-[#0b105d]/80 hover:text-[#0b105d] hover:bg-gray-50'
                  : 'text-[#0b105d]/80 hover:text-[#fe6d0d] hover:bg-[#fe6d0d]/5', // CHANGED to dark text
              ]"
              active-class="router-link-mobile-active"
              :style="{ animationDelay: `${index * 60}ms` }"
            >
              <!-- Active state remains good -->
              <div
                v-if="isMobileActive(item.path)"
                class="absolute inset-0 bg-gradient-to-r from-[#fe6d0d]/20 to-[#ffb47c]/20 rounded-lg sm:rounded-xl shadow-md"
              ></div>
              <div class="relative z-10 flex items-center justify-between">
                <span>{{ $t(item.name) }}</span>
                <ChevronRightIcon
                  class="w-4 h-4 sm:w-5 sm:h-5 transform group-hover:translate-x-1 transition-all duration-300"
                  :class="
                    isScrolled
                      ? 'text-[#0b105d]/40 group-hover:text-[#0b105d]/60'
                      : 'text-[#0b105d]/40 group-hover:text-[#0b105d]/60' // Text color matches the menu item text
                  "
                />
              </div>
            </router-link>
            <div
              class="pt-2 sm:pt-3 border-t"
              :class="isScrolled ? 'border-gray-200/50' : 'border-gray-300/50'"
            >
              <!-- CTA in mobile menu already has good contrast -->
              <router-link
                to="/contact"
                @click="mobileMenuOpen = false"
                class="cta-button group mobile-menu-item relative block w-full text-center px-4 sm:px-6 py-3 sm:py-3.5 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold text-white overflow-hidden shadow-lg transform hover:scale-[1.01] transition-all duration-300"
                :style="{ animationDelay: `${navItems.length * 60}ms` }"
              >
                <div class="absolute inset-0 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c]"></div>
                <div
                  class="absolute inset-0 bg-gradient-to-r from-[#ffb47c] to-[#fe6d0d] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                ></div>
                <div class="rotating-glow"></div>
                <span
                  class="relative z-10 flex items-center justify-center space-x-1.5 sm:space-x-2"
                >
                  <span>{{ $t('nav.getStarted') }}</span>
                  <SendIcon
                    class="w-3 h-3 sm:w-4 sm:h-4 transform group-hover:rotate-[15deg] transition-transform duration-300"
                  />
                </span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup lang="ts">
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'
import TorukLogo from '@/components/icons/ApplicationLogo.vue'
import { ArrowRightIcon, ChevronRightIcon, MenuIcon, SendIcon, XIcon } from 'lucide-vue-next'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const { t } = useI18n()
const route = useRoute()

const mobileMenuOpen = ref(false)
const scrollY = ref(0)

const navItems = [
  { name: 'nav.home', path: '/' },
  { name: 'nav.about', path: '/about' },
  { name: 'nav.contact', path: '/contact' },
]

// Computed properties
const isScrolled = computed(() => scrollY.value > 50)

const navbarClass = computed(() => ({
  'navbar-scrolled': isScrolled.value,
  'navbar-transparent': !isScrolled.value,
}))

const isActive = (path: string) => {
  return route.path === path
}

const isMobileActive = (path: string) => {
  return route.path === path
}

// Scroll handler
const handleScroll = () => {
  scrollY.value = window.scrollY
}


const handleOutsideClick = (event: Event) => {
  const target = event.target as HTMLElement

  // Check if the click is on the menu toggle button - if so, don't close the menu
  if (target.closest('button[aria-label="Toggle main menu"]')) {
    return
  }

  // Check if click is inside the mobile menu container
  if (target.closest('.mobile-menu-container')) {
    return
  }

  // If mobile menu is open and click is outside, close it
  if (mobileMenuOpen.value) {
    mobileMenuOpen.value = false
  }
}

const toggleMobileMenu = (event: Event) => {
  event.stopPropagation() // Prevent the click from bubbling up
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && mobileMenuOpen.value) {
    mobileMenuOpen.value = false
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  // Delay adding the outside click listener to prevent immediate conflicts
  setTimeout(() => {
    document.addEventListener('click', handleOutsideClick, true) // Use capture phase
  }, 100)
  document.addEventListener('keydown', handleEscapeKey)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('click', handleOutsideClick)
  document.removeEventListener('keydown', handleEscapeKey)
})
</script>

<style scoped>
.rotating-glow {
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  background: conic-gradient(from 0deg, #fe6d0d, #ffb47c, #fe6d0d);
  filter: blur(8px);
  opacity: 0;
  animation: rotateGlow 3s linear infinite paused;
  z-index: -1;
}

.cta-button:hover .rotating-glow {
  animation-play-state: running;
  opacity: 0.6;
}

@keyframes rotateGlow {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes gradient-x {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.animate-gradient-x {
  animation: gradient-x 15s ease infinite;
  background-size: 300% 300%;
}

.router-link-active > span {
  font-weight: 600;
}

.router-link-mobile-active > span {
  font-weight: 600;
}

.mobile-menu-item {
  animation-name: slideInUp;
  animation-duration: 0.4s;
  animation-fill-mode: forwards;
  animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  opacity: 0;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
